import { Injectable, inject } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Land } from '../../core/model/land';
import { WorkSchedule, ScheduledLand } from '../../core/model/work-schedule';
import { QuestionnairePackage } from '../../core/questionnaire-package';


interface WorkScheduleForm {
  _id: FormControl<string | null>;
  name: FormControl<string | null>;
  description: FormControl<string | null>;
  scheduledLand: FormArray<FormControl<{ idLand: string } | null>>;
  cropObjective: FormControl<string | null>;
  idQuestionnairePackage: FormControl<string[] | null>;
  questionnairePackage: FormArray<FormControl<QuestionnairePackage | null>>;
}

export interface WorkScheduleFormData {
  _id: string;
  name: string;
  description: string;
  scheduledLand: { idLand: string }[];
  cropObjective: string;
  idQuestionnairePackage: string[];
  questionnairePackage: QuestionnairePackage[];
}

@Injectable()
export class WorkScheduleFormService {
  private fb = inject(FormBuilder);

  private workScheduleForm!: FormGroup<WorkScheduleForm>;

  constructor() {
    this._initForm();

    // Update the idQuestionnairePackage list using the value of the helper property
    this.questionnairePackage.valueChanges.subscribe({
      next: (changedValue) => {
        const mappedValue = changedValue.map(value => {
          return value ? value._id : '';
        });
        this.idQuestionnairePackage.patchValue(mappedValue);
      }
    });
  }

  loadWorkSchedule(workSchedule: WorkSchedule): void {
    this._id.patchValue(workSchedule._id);
    this.name.patchValue(workSchedule.name);
    // this.startDate.patchValue(workSchedule);
    // this.endDate.patchValue(workSchedule.endDate);
    this.description.patchValue(workSchedule.description);
    this.cropObjective.patchValue(workSchedule.cropObjective);
    if (workSchedule.idQuestionnairePackage) {
      this.questionnairePackage.patchValue(workSchedule.idQuestionnairePackage);
    }
    this.setLands(workSchedule.scheduledLand);
  }

  private _initForm(): void {
    this.workScheduleForm = this.fb.group<WorkScheduleForm>({
      _id: this.fb.control(null),
      name: this.fb.control(null, [Validators.required]),
      description: this.fb.control(null, [Validators.required]),
      scheduledLand: this.fb.array<FormControl>([], [Validators.required]),
      cropObjective: this.fb.control(null, [Validators.required]),
      idQuestionnairePackage: this.fb.control([], [Validators.required ]),
      questionnairePackage: this.fb.array([
        this.fb.control<QuestionnairePackage | null>(null, [Validators.required]),
        this.fb.control<QuestionnairePackage | null>(null, [Validators.required]),
        this.fb.control<QuestionnairePackage | null>(null, [Validators.required]),
      ], [Validators.required]),
    });
  }

  addLand(landToAdd: Land): void {
    const value = this.landsValue;
    const index = value.findIndex(land => land.idLand === landToAdd._id);
    if (index === -1) {
      const control: FormControl = this.fb.control({ idLand: landToAdd._id });
      this.scheduledLand.push(control);
    }
  }

  setLands(lands: ScheduledLand[]): void {
    this.scheduledLand.clear();
    lands.forEach(land => {
      const control: FormControl = this.fb.control({ idLand: land.idLand as string });
      this.scheduledLand.push(control);
    });
  }

  removeLand(landToRemove: Land): void {
    const value = this.landsValue;
    const index = value.findIndex(land => land.idLand === landToRemove._id);
    if (index !== -1) {
      this.scheduledLand.removeAt(index);
    }
  }

  setQuestionnairePackageElement(index: number, questionnairePackage: QuestionnairePackage): void {
    this.questionnairePackage.at(index).patchValue(questionnairePackage);
  }

  emptyQuestionnairePackageElement(index: number): void {
    this.questionnairePackage.at(index).patchValue(null);
  }

  get form(): FormGroup<WorkScheduleForm> {
    return this.workScheduleForm;
  }

  get value(): WorkScheduleFormData {
    return this.workScheduleForm.value as WorkScheduleFormData;
  }

  get valid(): boolean {
    return this.workScheduleForm.valid;
  }

  get _id(): FormControl<string> {
    return this.workScheduleForm.get('_id') as FormControl<string>;
  }

  get name(): FormControl<string> {
    return this.workScheduleForm.get('name') as FormControl<string>;
  }

  get description(): FormControl<string> {
    return this.workScheduleForm.get('description') as FormControl<string>;
  }

  get scheduledLand(): FormArray<FormControl<{ idLand: string }>> {
    return this.workScheduleForm.get('scheduledLand') as FormArray<FormControl<{ idLand: string }>>;
  }

  get cropObjective(): FormControl<string> {
    return this.workScheduleForm.get('cropObjective') as FormControl<string>;
  }

  get idQuestionnairePackage(): FormArray<FormControl<string>> {
    return this.workScheduleForm.get('idQuestionnairePackage') as FormArray<FormControl<string>>;
  }

  get questionnairePackage(): FormArray<FormControl<QuestionnairePackage | null>> {
    return this.workScheduleForm.get('questionnairePackage') as FormArray<FormControl<QuestionnairePackage | null>>;
  }

  get landsValue(): { idLand: string }[] {
    return this.scheduledLand.value;
  }
}
